import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import type { DistribItem } from '../types'

// Rampas monocromáticas de tonos Tailwind (oscuro → claro): cada columna se tiñe
// según su cantidad (mayor = más oscuro), mismo criterio que en registros.
const PALETAS: Record<string, string[]> = {
  // Azul corporativo → blue-800…blue-100
  '#57a6d8': ['#1e40af', '#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'],
  // Verde corporativo → emerald-800…emerald-100
  '#2fbc80': ['#065f46', '#047857', '#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5'],
  // Morado corporativo → violet-800…violet-100
  '#8d82bc': ['#5b21b6', '#6d28d9', '#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe'],
  // Rosa corporativo → rose-800…rose-100
  '#eb6587': ['#9f1239', '#be123c', '#e11d48', '#f43f5e', '#fb7185', '#fda4af', '#fecdd3', '#ffe4e6'],
}

interface VerticalBarChartProps {
  data: DistribItem[]
  /** Color hex corporativo; selecciona la rampa monocromática de la gráfica */
  color: string
  /** Etiqueta de la serie (tooltip/leyenda) */
  seriesLabel?: string
  /** Alto fijo de la gráfica en px (incluye el eje de categorías) */
  height?: number
  /** Ancho mínimo por columna (px). Con muchas categorías el gráfico hace scroll horizontal */
  slotWidth?: number
  /** Grosor máximo de columna */
  maxBarSize?: number
  /** Formateo del valor mostrado sobre la columna */
  valueFormatter?: (v: number) => string
  /** Fija el máximo del eje Y (útil para porcentajes: yMax=100) */
  yMax?: number
}

const defaultFormatter = (v: number) => v.toLocaleString('es-EC')

export function VerticalBarChart({
  data,
  color,
  seriesLabel = 'Total',
  height = 240,
  slotWidth = 56,
  maxBarSize = 44,
  valueFormatter = defaultFormatter,
  yMax,
}: VerticalBarChartProps) {
  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-muted-foreground"
        style={{ height }}
      >
        Sin datos para mostrar.
      </div>
    )
  }

  // Rampa de la gráfica (fallback: color plano si el hex no está mapeado).
  const paleta = PALETAS[color] ?? [color]

  // Tono por columna en función de la cantidad: el valor más alto usa el tono más
  // oscuro (índice 0) y el más bajo el más claro.
  const valores = data.map((d) => d.valor)
  const min = Math.min(...valores)
  const max = Math.max(...valores)
  const colorPorValor = (valor: number) => {
    if (max === min) return paleta[0]
    const t = (valor - min) / (max - min) // 0 (menor) … 1 (mayor)
    const idx = Math.round((1 - t) * (paleta.length - 1))
    return paleta[idx]
  }

  const config = {
    valor: { label: seriesLabel, color: paleta[0] },
  } satisfies ChartConfig

  // Etiquetas inclinadas cuando hay muchas categorías o nombres largos.
  const TRUNCADO = 22 // máx. caracteres antes de recortar con «…»
  const maxLabelLen = Math.max(...data.map((d) => d.label.length))
  const inclinado = data.length > 6 || maxLabelLen > 10

  // Dimensiones del texto rotado (-35°) para reservar espacio y evitar recortes:
  // el ancho aproximado de la etiqueta más larga se proyecta sobre los ejes X e Y.
  const ANGULO = 35
  const rad = (ANGULO * Math.PI) / 180
  const anchoEtiqueta = Math.min(maxLabelLen, TRUNCADO) * 6.3 // ~6.3px por char a 11px
  const alturaEjeX = inclinado ? Math.ceil(anchoEtiqueta * Math.sin(rad)) + 20 : 28
  // Margen izquierdo: la etiqueta del primer bar (anclada al final) se extiende
  // hacia la izquierda; sin este espacio el contenedor la recorta.
  const margenIzq = inclinado ? Math.ceil(anchoEtiqueta * Math.cos(rad)) : 8

  const ejeX = inclinado
    ? { angle: -35 as const, textAnchor: 'end' as const, height: alturaEjeX }
    : { height: alturaEjeX }

  // Ancho interno: fuerza scroll horizontal solo si excede el contenedor.
  const innerWidth = data.length * slotWidth + margenIzq

  return (
    <div className="w-full overflow-x-auto">
      <div style={{ width: innerWidth, minWidth: '100%' }}>
        <ChartContainer config={config} className="!aspect-auto w-full" style={{ height }}>
          <BarChart accessibilityLayer data={data} margin={{ top: 20, right: 8, left: margenIzq, bottom: 0 }}>
            <XAxis
              dataKey="label"
              type="category"
              tickLine={false}
              axisLine={false}
              interval={0}
              tickMargin={8}
              tick={{ fontSize: 11 }}
              tickFormatter={(value: string) =>
                value.length > TRUNCADO ? value.slice(0, TRUNCADO) + '…' : value
              }
              {...ejeX}
            />
            <YAxis type="number" hide domain={yMax !== undefined ? [0, yMax] : undefined} />
            <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
            <Bar dataKey="valor" radius={[5, 5, 0, 0]} maxBarSize={maxBarSize}>
              {data.map((item, index) => (
                <Cell key={index} fill={colorPorValor(item.valor)} />
              ))}
              <LabelList
                dataKey="valor"
                position="top"
                offset={6}
                className="fill-foreground"
                fontSize={11}
                formatter={valueFormatter}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </div>
    </div>
  )
}
